import { inject, Pipe, PipeTransform } from '@angular/core';
import { BazisConfigurationService } from '@bazis/configuration.service';

/**
 * Converts an array of enum values to a single, human-readable string by
 * resolving labels from API schemas via `BazisConfigurationService`.
 * Use it to display multiple enum values (e.g., roles, tags) in templates.
 *
 * How it works:
 * - Finds the attribute schema for the given entity `type` and `property`.
 * - Reads `enumDict` and maps each value to its label.
 * - Joins labels with `delimiter`. Returns `null` when nothing to show.
 *
 * Dependencies:
 * - `BazisConfigurationService` (via `inject()`). Ensure schemas are loaded on app start
 *   by calling `BazisConfigurationService.loadConfiguration()`. The pipe expects a key
 *   in `schemas` like `${type.replace('.', '__')}__schema_retrieve` that contains the
 *   attribute with `enumDict`.
 *
 * Signature:
 * `transform(enumValues: string[] | null | undefined, type: string, property: string, delimiter = ', '): string | null`
 *
 * Parameters:
 * - `enumValues`: Array of internal enum values from API (e.g., `['admin','editor']`).
 * - `type`: Entity type in dot notation (e.g., `'user.user'`).
 * - `property`: Attribute name in the schema (e.g., `'roles'`).
 * - `delimiter`: String used to join labels. Default: `", "`.
 *
 * Examples:
 * ```html
 * <!-- Single entity field: cache with @let, guard with @if -->
 * @let roles = (user.$snapshot.roles | enums:'user.user':'roles');
 * @if (roles) {
 *   {{ roles }}
 * } @else {
 *   <span>-</span>
 * }
 *
 * <!-- Custom delimiter -->
 * @let tags = (item.$snapshot.tags | enums:'catalog.item':'tags':' / ');
 * {{ tags || '-' }}
 *
 * <!-- In a list -->
 * @for (user of users; track user.id) {
 *   @let labels = (user.$snapshot.permissions | enums:'user.user':'permissions');
 *   <div>{{ user.$snapshot.name }} — {{ labels || '-' }}</div>
 * }
 *
 * <!-- Defensive: empty/undefined list returns null -->
 * @let labels = (undefined | enums:'user.user':'roles');
 * {{ labels || '-' }}
 * ```
 */
@Pipe({
    name: 'enums',
    standalone: true,
})
export class EnumsPipe implements PipeTransform {
    protected configurationService = inject(BazisConfigurationService);

    /**
     * Map enum values to labels and join them by delimiter.
     *
     * @param enumValues - List of internal enum values from API.
     * @param type - Entity type in dot notation (e.g., 'user.user').
     * @param property - Attribute name (e.g., 'roles').
     * @param delimiter - Join delimiter, default is ', '.
     * @returns Joined labels or null when empty/undefined.
     */
    transform(enumValues: string[], type: string, property: string, delimiter = ', ') {
        type = type.replace('.', '__');
        const attribute =
            this.configurationService.schemas[`${type}__schema_retrieve`].attributes[property];
        return enumValues?.length
            ? enumValues.map((enumValue) => attribute.enumDict[enumValue]).join(delimiter)
            : null;
    }
}
